/**
 * Invitation Management Hooks
 * TanStack Query hooks for invitation operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as invitationService from '@/services/invitation.service';
import type { Invitation, CreateInvitationInput, AcceptInvitationInput } from '@/services/invitation.service';

/**
 * Admin: Query hook to get pending invitations
 */
export const usePendingInvitations = () => {
  return useQuery({
    queryKey: ['invitations', 'pending'],
    queryFn: () => invitationService.getPendingInvitations(),
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Admin: Query hook to get invitation statistics
 */
export const useInvitationStats = () => {
  return useQuery({
    queryKey: ['invitations', 'stats'],
    queryFn: () => invitationService.getInvitationStats(),
    staleTime: 60 * 1000, // 1 minute
  });
};

/**
 * Admin: Mutation hook to create and send an invitation
 */
export const useCreateInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateInvitationInput) => invitationService.createInvitation(input),
    onSuccess: (newInvitation) => {
      // Add to pending invitations list
      queryClient.setQueryData<Invitation[]>(['invitations', 'pending'], (old) => {
        if (!old) return [newInvitation];
        return [newInvitation, ...old];
      });

      // Invalidate stats to refetch
      queryClient.invalidateQueries({ queryKey: ['invitations', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['invitations', 'pending'] });
    },
  });
};

/**
 * Admin: Mutation hook to resend invitation email
 */
export const useResendInvitation = () => {
  return useMutation({
    mutationFn: (invitationId: string) => invitationService.resendInvitation(invitationId),
  });
};

/**
 * Admin: Mutation hook to revoke an invitation
 */
export const useRevokeInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invitationId: string) => invitationService.revokeInvitation(invitationId),
    onSuccess: (_, invitationId) => {
      // Remove from pending invitations list
      queryClient.setQueryData<Invitation[]>(['invitations', 'pending'], (old) => {
        if (!old) return [];
        return old.filter((inv) => inv.id !== invitationId);
      });

      // Invalidate stats to refetch
      queryClient.invalidateQueries({ queryKey: ['invitations', 'stats'] });
    },
  });
};

/**
 * Admin: Mutation hook to test SMTP connection
 */
export const useTestSmtp = () => {
  return useMutation({
    mutationFn: () => invitationService.testSmtpConnection(),
  });
};

/**
 * Public: Query hook to validate an invitation token
 */
export const useValidateInvitation = (token: string | null) => {
  return useQuery({
    queryKey: ['invitation', 'validate', token],
    queryFn: () => invitationService.validateToken(token!),
    enabled: !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false, // Don't retry on invalid token
  });
};

/**
 * Public: Mutation hook to accept an invitation
 */
export const useAcceptInvitation = () => {
  return useMutation({
    mutationFn: ({ token, input }: { token: string; input: AcceptInvitationInput }) =>
      invitationService.acceptInvitation(token, input),
  });
};

/**
 * Hook to get SMTP configuration status
 */
export const useSMTPStatus = () => {
  return useQuery({
    queryKey: ['smtp', 'status'],
    queryFn: () => invitationService.getSMTPStatus(),
    staleTime: 60 * 1000, // 1 minute
  });
};
